import { motion } from "framer-motion";
import { CHAPTERS } from "../data/services";
import { ServiceGraphic } from "./graphics";
import { FloatItem, Twinkle } from "./FloatingDecor";

const EASE = [0.16, 1, 0.3, 1];

const fadeUp = {
  hidden: { opacity: 0, y: 34 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: EASE } },
};

const HEART_P =
  "M12 21c-4.6-3.7-9-7-9-11.2C3 6.6 5.4 4.5 8 4.5c1.6 0 3.1.8 4 2.1.9-1.3 2.4-2.1 4-2.1 2.6 0 5 2.1 5 5.3 0 4.2-4.4 7.5-9 11.2z";
const STAR_P = "M12 2l2.2 7.8L22 12l-7.8 2.2L12 22l-2.2-7.8L2 12l7.8-2.2L12 2z";

const BurstParticles = () => (
  <>
    <svg viewBox="0 0 24 24" className="burst-particle burst-1 w-3.5 h-3.5" fill="#D88C9A" aria-hidden="true">
      <path d={HEART_P} />
    </svg>
    <svg viewBox="0 0 24 24" className="burst-particle burst-2 w-3 h-3" fill="#D4AF37" aria-hidden="true">
      <path d={STAR_P} />
    </svg>
    <svg viewBox="0 0 24 24" className="burst-particle burst-3 w-3 h-3" fill="#E0A3AC" aria-hidden="true">
      <path d={HEART_P} />
    </svg>
    <svg viewBox="0 0 24 24" className="burst-particle burst-4 w-3.5 h-3.5" fill="#D4AF37" aria-hidden="true">
      <path d={STAR_P} />
    </svg>
    <svg viewBox="0 0 24 24" className="burst-particle burst-5 w-3 h-3" fill="#C57B89" aria-hidden="true">
      <path d={HEART_P} />
    </svg>
  </>
);

const CustomComposition = () => (
  <div className="relative mt-12 w-56 h-64" aria-hidden="true">
    <div className="absolute inset-x-6 top-2 bottom-2 rounded-t-[999px] bg-gradient-to-b from-[#F3D9DC] via-[#F9F6F0] to-[#FDFBF7] border border-[#D4AF37]/30" />
    <motion.div
      animate={{ y: [0, -12, 0] }}
      transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      className="absolute left-0 top-16 w-20 h-20 rounded-full bg-[#D88C9A]/15 border border-[#D88C9A]/30 backdrop-blur-sm"
    />
    <motion.div
      animate={{ y: [0, 12, 0] }}
      transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
      className="absolute right-0 bottom-14 w-14 h-14 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40"
    />
    <motion.svg
      viewBox="0 0 24 24"
      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10"
      fill="#D88C9A"
      animate={{ scale: [1, 1.12, 1] }}
      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
    >
      <path d="M12 21c-4.6-3.7-9-7-9-11.2C3 6.6 5.4 4.5 8 4.5c1.6 0 3.1.8 4 2.1.9-1.3 2.4-2.1 4-2.1 2.6 0 5 2.1 5 5.3 0 4.2-4.4 7.5-9 11.2z" />
    </motion.svg>
  </div>
);

const Chapter = ({ chapter, flip }) => (
  <div className={flip ? "bg-[#F9F6F0]" : "bg-[#FDFBF7]"}>
    <div className="max-w-6xl mx-auto px-6 py-24 md:py-32 grid md:grid-cols-12 gap-12 md:gap-16">
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        className={`relative md:col-span-5 ${flip ? "md:order-2" : ""}`}
      >
        <Twinkle className="w-4 h-4 top-6 right-10" delay={flip ? 1.2 : 0.4} />
        <FloatItem type="heart" filled color="#E0A3AC" className="w-4 h-4 top-40 -left-1" delay={0.9} duration={9} drift={10} sway={6} spin={10} />
        <span className="font-serif text-[6rem] md:text-[8rem] leading-none stroke-number select-none">
          {chapter.num}
        </span>
        <h3 className="font-serif text-4xl md:text-5xl font-light tracking-tight mt-4 text-[#2C2421]">
          {chapter.title}
        </h3>
        <motion.div
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: EASE, delay: 0.35 }}
          className="origin-left h-px w-24 mt-4 bg-gradient-to-r from-[#D88C9A] to-[#D4AF37]"
        />
        <p className="font-serif italic text-lg text-[#C57B89] mt-3">{chapter.tagline}</p>
        {chapter.image ? (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1.1, ease: EASE, delay: 0.15 }}
            className="group relative mt-12 w-56 md:w-64"
          >
            <div className="rounded-t-[999px] overflow-hidden border border-[#D4AF37]/25 shadow-[0_30px_60px_-30px_rgba(216,140,154,0.45)]">
              <img
                src={chapter.image}
                alt={chapter.imageAlt}
                loading="lazy"
                className="w-full aspect-[3/4] object-cover transition-transform duration-[1.6s] ease-out hover:scale-105"
              />
            </div>
          </motion.div>
        ) : (
          <CustomComposition />
        )}
      </motion.div>

      <div className={`md:col-span-7 flex flex-col justify-center gap-14 ${flip ? "md:order-1" : ""}`}>
        {chapter.services.map((s, i) => (
          <motion.div
            key={s.slug}
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            transition={{ delay: i * 0.08 }}
            className="group flex items-start gap-6"
            data-testid={`service-${s.slug}`}
          >
            <div className="relative shrink-0 w-24 h-24 transition-all duration-500 group-hover:-translate-y-1.5 group-hover:rotate-1 flex items-center justify-center text-[#C57B89]">
              <div
                className={`tile-shine absolute inset-0 ${
                  i % 2 === 1 ? "rounded-t-[2.6rem] rounded-b-[1.6rem]" : "rounded-[1.6rem]"
                } bg-gradient-to-br from-white via-[#FDFBF7] to-[#F3D9DC] border border-[#E8E1D9] shadow-[0_18px_40px_-20px_rgba(216,140,154,0.55)] transition-shadow duration-500 group-hover:shadow-[0_28px_50px_-18px_rgba(212,175,55,0.6)]`}
              >
                <div
                  aria-hidden="true"
                  className="absolute inset-0"
                  style={{
                    background:
                      "radial-gradient(circle at 28% 22%, rgba(255,255,255,0.95), transparent 55%)",
                  }}
                />
              </div>
              <ServiceGraphic
                slug={s.slug}
                className="relative w-12 h-12 transition-transform duration-700 group-hover:scale-110 group-hover:-rotate-3"
              />
              <BurstParticles />
            </div>
            <div>
              <h4 className="font-serif text-2xl md:text-[1.7rem] font-light text-[#2C2421]">
                {s.name}
              </h4>
              <p className="text-[#8A7D78] font-light mt-1">{s.desc}</p>
              <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5">
                {s.items.map((it) => (
                  <li
                    key={it}
                    className="flex items-center gap-2 text-sm font-light text-[#2C2421]/65"
                  >
                    <span className="w-1 h-1 rotate-45 bg-[#D4AF37]" aria-hidden="true" />
                    {it}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </div>
);

export default function Chapters() {
  return (
    <section id="services" data-testid="services-section" className="relative">
      <div className="relative max-w-6xl mx-auto px-6 pt-28 md:pt-36 pb-4">
        <FloatItem type="gift" className="w-10 h-10 right-[8%] top-24 text-[#C57B89] hidden md:block" delay={0.6} duration={10} drift={14} sway={8} />
        <FloatItem type="hamper" className="w-9 h-9 right-[16%] top-40 text-[#E0A3AC] hidden md:block" delay={1} duration={11} drift={12} sway={7} />
        <motion.p
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="text-[0.65rem] tracking-[0.5em] uppercase text-[#8A7D78]"
        >
          What we're crafting
        </motion.p>
        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="font-serif text-4xl md:text-6xl font-light tracking-tight mt-5 text-[#2C2421]"
        >
          Five little worlds,
          <br />
          <em className="text-givi-gradient not-italic font-medium italic">one beautiful idea.</em>
        </motion.h2>
      </div>
      {CHAPTERS.map((ch, idx) => (
        <Chapter key={ch.num} chapter={ch} flip={idx % 2 === 1} />
      ))}
    </section>
  );
}
